// Keyword Performance Dashboard — the PRD's six sub-features.
//
// One table and one second list, exactly as the PRD frames it. Every
// sub-feature is a column, a sort, a filter or an expansion on the same rows,
// so moving between the filters changes the question, not the subject.
//
// Separate from Search Term Performance on purpose. The two documents overlap
// on the ranked list and the conversion figure and nowhere else: the branded
// split and cannibalization belong to that PRD and never appear here, and
// movement, the leak stage, the opportunity and absent lists, competitor wins
// and the effectiveness score belong to this one and never appear there. Both
// read the same rollup, so a term carries identical figures on either page.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiArrowLeft, FiLink, FiLock } from 'react-icons/fi';

import shell from '../SellerIntelligence.module.css';
import s from './KeywordPerformance.module.css';
import AuthContext from '../../../context/AuthContext';
import { useAmazonStatus } from '../../../hooks/useAmazonStatus';
import FeatureCards from './components/FeatureCards';
import KeywordTable, { EXTRA_COLUMNS, EXTRAS_FOR_VIEW } from './components/KeywordTable';
import WhatChanged from '../shared/WhatChanged';
import AbsentList from './components/AbsentList';
import { NotConnectedError, getChanges, getProducts, getAbsent, getOverview, getTable, getTerm,
         reclassify } from './api';
import { num } from './format';

// The PRD's filters. Generic is the default: branded terms carry demand the
// seller already earned and inflate every share they appear in.
// "All terms" is not one of the PRD's filters. It is here because the cards
// are computed over every term in the window, and a card reading "700 terms
// ranked" that opens a 646-row generic table contradicts itself the moment it
// is clicked. Generic remains the view the page opens on.
const FILTERS = [
  { id: 'all', label: 'All terms', ref: '' },
  { id: 'generic', label: 'Generic', ref: 'default' },
  { id: 'branded', label: 'Branded', ref: '' },
  { id: 'slipping', label: 'Slipping', ref: 'SF1' },
  { id: 'opportunity', label: 'Opportunity', ref: 'SF4' },
  { id: 'competitor_wins', label: 'Competitor wins', ref: 'SF5' },
  // The PRD routes this through Pricing Strategy Analyzer, which does not
  // exist. The reading is on every row already, and the document's own rule is
  // that a sub-feature is a filter on one table rather than a screen of its
  // own — so it lives here until Pricing ships and can link straight to it.
  { id: 'price_gap', label: 'Price gap', ref: 'SF3' },
];

const SORTS = [
  ['clicks', 'Clicks'], ['purchases', 'Purchases'], ['volume', 'Search volume'],
  ['impressions', 'Impressions'], ['impression_share', 'Impression share'],
  ['click_share', 'Click share'], ['cart_add_share', 'Cart-add share'],
  ['purchase_share', 'Purchase share'], ['effectiveness', 'Effectiveness'],
];

// Which card opens what. SF4 carries both halves of one question, so it opens
// the on-table filter and leaves the absent list one click away.
const CARD_TARGET = {
  ranking: { tab: 'table', view: 'all' },
  volume: { tab: 'table', view: 'all', sort: 'volume' },
  conversion: { tab: 'table', view: 'all', sort: 'purchase_share' },
  opportunity: { tab: 'table', view: 'opportunity' },
  gap: { tab: 'table', view: 'competitor_wins' },
  score: { tab: 'table', view: 'all', sort: 'effectiveness' },
};

export default function KeywordPerformance() {
  const navigate = useNavigate();
  // Arriving from a product elsewhere — Listing Score names a listing, and the
  // terms behind it are the next question. The filter is read once on mount so
  // a later change of view does not silently drop it.
  const [searchParams] = useSearchParams();
  const [productFilter, setProductFilter] = useState(searchParams.get('asins') || '');
  const [products, setProducts] = useState({ products: [], without_terms: 0 });
  const [changes, setChanges] = useState(null);
  const { token } = useContext(AuthContext);
  const { connected, loading: checking } = useAmazonStatus(token);

  const [tab, setTab] = useState('table');
  const [period, setPeriod] = useState('WEEK');
  const [startDate, setStartDate] = useState('');
  const [windowOptions, setWindowOptions] = useState([]);

  const [overview, setOverview] = useState(null);
  const [table, setTable] = useState(null);
  const [absent, setAbsent] = useState(null);

  const [view, setView] = useState('generic');  // the PRD's default view
  // The PRD columns that are off by default. A filter that needs one to be
  // readable switches it on; from there the seller owns the choice.
  const [extras, setExtras] = useState(EXTRAS_FOR_VIEW.generic || []);
  const [sort, setSort] = useState('clicks');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [locked, setLocked] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notConnected, setNotConnected] = useState(false);

  const fail = useCallback((e) => {
    if (e instanceof NotConnectedError) setNotConnected(true);
    else setError(e.message);
  }, []);

  // The cards describe the window and the sort, because movement is recomputed
  // per sort and SF1's headline is about movement.
  useEffect(() => {
    if (!token || !connected) return undefined;
    let live = true;
    getOverview(token, { period, start_date: startDate, sort })
      .then((r) => {
        if (!live) return;
        setOverview(r);
        setWindowOptions(r.window_options || []);
        setLocked(r.locked ? r : null);
        if (r.start_date && !startDate) setStartDate(r.start_date);
      })
      .catch((e) => { if (live) fail(e); });
    return () => { live = false; };
  }, [token, connected, period, startDate, sort, fail]);

  const load = useCallback(async () => {
    if (!token || !connected) return;
    setLoading(true);
    setError(null);
    try {
      const args = { period, start_date: startDate };
      if (tab === 'absent') setAbsent(await getAbsent(token, { ...args, limit: 200 }));
      else if (tab === 'changed') setChanges(await getChanges(token, args));
      else {
        setTable(await getTable(token, {
          ...args, view, sort, search: search.trim(),
          asins: productFilter || undefined, page, page_size: 50 }));
      }
    } catch (e) { fail(e); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, connected, tab, period, startDate, view, sort, page, productFilter]);

  useEffect(() => { load(); }, [load]);

  // The products that have terms in this window, so a product can be chosen
  // here rather than only arrived at with the filter already in the URL. It
  // follows the window, because which products Amazon showed changes with it.
  useEffect(() => {
    if (!token || !connected) return;
    getProducts(token, { period, start_date: startDate })
      .then(setProducts)
      .catch(() => setProducts({ products: [], without_terms: 0 }));
  }, [token, connected, period, startDate]);

  const expand = async (term) => {
    setExpanded(term);
    setDetail(null);
    setDetailLoading(true);
    try {
      const r = await getTerm(token, term, { period, start_date: startDate });
      setDetail(r.term ? { ...r.term, trend: r.trend } : null);
    } catch (e) { fail(e); } finally { setDetailLoading(false); }
  };

  const correct = async (term, label) => {
    try {
      await reclassify(token, term, label);
      load();
    } catch (e) { fail(e); }
  };

  // Changing the filter changes which columns the finding needs, so the
  // defaults follow it rather than stranding the seller on a list whose
  // subject is in a column they have to know to turn on.
  const changeView = (next) => {
    setView(next);
    setExtras(EXTRAS_FOR_VIEW[next] || []);
    setPage(1);
  };

  const toggleExtra = (id) =>
    setExtras((on) => (on.includes(id) ? on.filter((x) => x !== id) : [...on, id]));

  const openCard = (key) => {
    const target = CARD_TARGET[key] || { tab: 'table', view: 'generic' };
    setTab(target.tab);
    if (target.view) changeView(target.view);
    if (target.sort) setSort(target.sort);
    setPage(1);
  };

  function body() {
    if (checking) return <div className={s.empty}>Checking connection…</div>;
    if (!connected || notConnected) {
      return (
        <div className={s.locked}>
          <div className={s.lockedIcon}><FiLink /></div>
          <h2 className={s.lockedTitle}>Connect Amazon</h2>
          <p className={s.lockedBody}>Keyword data comes from your Amazon account.</p>
          <button className={s.button} onClick={() => navigate('/dashboard/market-places')}>
            Connect Amazon
          </button>
        </div>
      );
    }

    return (
      <div className={s.page}>
        {error && <div className={s.error}>{error}</div>}

        <section className={s.panel}>
          <div className={s.contextBar}>
            <div className={s.contextLeft}>
              <label className={s.field}>
                <span className={s.label}>Period</span>
                <select className={s.select} value={period}
                        onChange={(e) => { setPeriod(e.target.value); setStartDate(''); setPage(1); }}>
                  {['WEEK', 'MONTH', 'QUARTER'].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label className={s.field}>
                <span className={s.label}>Window</span>
                <select className={s.select} value={startDate}
                        onChange={(e) => { setStartDate(e.target.value); setPage(1); }}>
                  {!windowOptions.length && <option value="">No data yet</option>}
                  {windowOptions.map((w) => (
                    <option key={w.start_date} value={w.start_date}>
                      {w.start_date}{w.collected ? '' : ' — not collected'}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {overview?.coverage && (
              <div className={overview.coverage.complete ? s.coverage : s.coverageWarn}>
                {num(overview.coverage.products_with_data)} of{' '}
                {num(overview.coverage.products_in_catalogue)} products have keyword data
                for this window
                {overview.coverage.products_at_term_cap > 0 && (
                  <> · {num(overview.coverage.products_at_term_cap)} returned the maximum{' '}
                    {num(overview.coverage.terms_per_product_cap)} terms, so their tail is
                    cut and these totals are a floor</>
                )}
                {overview.has_market_data === false && (
                  <> · the marketplace Top Search Terms report is not collected for this
                    window, so concentration, who wins a term and the absent list are
                    unavailable here</>
                )}
              </div>
            )}
          </div>
        </section>

        {!locked && (
          <FeatureCards data={overview} loading={loading && !overview} onOpen={openCard} />
        )}

        <section className={s.panel}>
          <div className={s.tabs}>
            <button className={tab === 'table' ? s.tabActive : s.tab}
                    onClick={() => { setTab('table'); setPage(1); }}>
              Keyword table<span className={s.tabRef}>SF1-SF6</span>
            </button>
            <button className={tab === 'absent' ? s.tabActive : s.tab}
                    onClick={() => { setTab('absent'); setPage(1); }}>
              Terms you are absent from<span className={s.tabRef}>SF4</span>
            </button>
            {/* A term that slipped is SF1 and a term a rival took is SF5, so
                what changed about terms belongs here rather than on a setup
                page that answers neither question. */}
            <button className={tab === 'changed' ? s.tabActive : s.tab}
                    onClick={() => { setTab('changed'); setPage(1); }}>
              What changed<span className={s.tabRef}>SF1 · SF5</span>
            </button>
          </div>

          {locked ? (
            <div className={s.locked}>
              <div className={s.lockedIcon}><FiLock /></div>
              <h2 className={s.lockedTitle}>Keyword data is not available yet</h2>
              <p className={s.lockedBody}>
                Amazon serves these reports to the owner of a brand in Brand Registry. An
                authorised seller of someone else&apos;s brand is refused even when the
                account appears in Brand Registry.
              </p>
            </div>
          ) : tab === 'absent' ? (
            <div style={{ paddingTop: 16 }}>
              <AbsentList data={absent} loading={loading && !absent} />
            </div>
          ) : tab === 'changed' ? (
            <div style={{ paddingTop: 16 }}>
              <WhatChanged data={changes} styles={s} onOpen={expand}
                           emptyLine="No term slipped far enough, and no named competitor took one, since the window before." />
            </div>
          ) : (
            <div style={{ paddingTop: 16 }}>
              <div className={s.filters}>
                {FILTERS.map((f) => (
                  <button key={f.id} className={view === f.id ? s.chipActive : s.chip}
                          onClick={() => changeView(f.id)}>
                    {f.label}{f.ref && <span className={s.chipRef}>{f.ref}</span>}
                  </button>
                ))}
              </div>

              {productFilter && (
                <div className={s.productFilter}>
                  Showing only the terms <strong>{productFilter}</strong> competes on —
                  every figure below is that product&apos;s, not the catalogue&apos;s.
                  <button className={s.clearFilter}
                          onClick={() => { setProductFilter(''); setPage(1); }}>
                    Show the whole catalogue
                  </button>
                </div>
              )}

              <div className={s.columnPicker}>
                <span className={s.label}>Columns</span>
                {EXTRA_COLUMNS.map((c) => (
                  <button key={c.id}
                          className={extras.includes(c.id) ? s.colOn : s.colOff}
                          onClick={() => toggleExtra(c.id)}>
                    {c.label}<span className={s.chipRef}>{c.ref}</span>
                  </button>
                ))}
              </div>

              <div className={s.controls} style={{ marginBottom: 14 }}>
                {/* A product, chosen here. The PRD routes this in from Listing
                    Intelligence, which does not exist — and a seller who wants
                    one product's terms should not have to arrive from another
                    feature to ask for them. */}
                <label className={s.field} style={{ minWidth: 260 }}>
                  <span className={s.label}>Product</span>
                  <select className={s.select} value={productFilter}
                          onChange={(e) => { setProductFilter(e.target.value); setPage(1); }}>
                    <option value="">
                      All products{products.products.length
                        ? ` (${products.products.length} with terms)` : ''}
                    </option>
                    {products.products.map((p) => (
                      <option key={p.asin} value={p.asin}>
                        {p.asin} · {p.terms} terms
                        {p.title ? ` — ${p.title.slice(0, 44)}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label className={s.field}>
                  <span className={s.label}>Sort by</span>
                  <select className={s.select} value={sort}
                          onChange={(e) => { setSort(e.target.value); setPage(1); }}>
                    {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>
                <label className={s.field}>
                  <span className={s.label}>Search</span>
                  <input className={s.input} value={search} placeholder="Filter terms…"
                         onChange={(e) => setSearch(e.target.value)}
                         onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load(); } }} />
                </label>
                <button className={s.secondary} onClick={() => { setPage(1); load(); }}>
                  Apply
                </button>
              </div>

              <p className={s.hint} style={{ marginBottom: 12 }}>
                Rank is position among your own terms on the sort above, and movement is
                recomputed on that same sort — never a product&apos;s position on
                Amazon&apos;s results page, which Amazon publishes no figure for.
              </p>

              {table?.note && <p className={s.notice}>{table.note}</p>}

              {view === 'opportunity' && table?.thresholds?.volume_at_least != null && (
                <p className={s.hint} style={{ marginBottom: 12 }}>
                  At least {num(table.thresholds.volume_at_least)} searches — the top{' '}
                  {100 - table.thresholds.volume_percentile}% of your terms this window —
                  and at most{' '}
                  {(table.thresholds.purchase_share_at_most * 100).toFixed(0)}% of the
                  term&apos;s purchases
                  {table.thresholds.concentration_at_most != null && (
                    <>, with the top three ASINs holding at most{' '}
                      {(table.thresholds.concentration_at_most * 100).toFixed(0)}% of its
                      clicks</>
                  )}. Bars are set against your own distribution in this window rather than
                  chosen. {table.thresholds.basis}
                </p>
              )}

              {loading ? <div className={s.empty}>Loading…</div>
                : !table?.terms?.length ? (
                  <div className={s.empty}>
                    {table?.window_collected === false
                      ? 'This window has not been collected.'
                      : 'No terms match this filter for this window.'}
                  </div>
                ) : (
                  <>
                    <KeywordTable rows={table.terms} showScore={table.score_available}
                                  extras={extras} onExpand={expand} expanded={expanded}
                                  detail={detail} detailLoading={detailLoading}
                                  onCorrect={correct} />
                    <div className={s.pagination}>
                      <span>
                        {num(table.pagination.total)} terms · page {table.pagination.page}
                        {' '}of {table.pagination.total_pages}
                        {table.compared_to && ` · compared with ${table.compared_to}`}
                      </span>
                      <button className={s.pageButton} disabled={page <= 1}
                              onClick={() => setPage(page - 1)}>Previous</button>
                      <button className={s.pageButton}
                              disabled={page >= table.pagination.total_pages}
                              onClick={() => setPage(page + 1)}>Next</button>
                    </div>
                  </>
                )}
            </div>
          )}
        </section>
      </div>
    );
  }

  return (
    <div className={shell.featureContainer}>
      <div className={shell.header}>
        <button className={shell.backButton}
                onClick={() => navigate('/dashboard/seller-intelligence')}>
          <FiArrowLeft /> Back
        </button>
        <div className={shell.titleSection}>
          <div className={shell.iconTitle}><h1>Keyword Performance</h1></div>
          <p>Rank your keywords, diagnose where each one leaks, find the gaps, score the list</p>
        </div>
      </div>
      <div className={shell.content}>{body()}</div>
    </div>
  );
}
